import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  CheckCircle2,
  Circle,
  Clock,
  Home,
  Car,
  FileText,
  CreditCard,
  Phone,
  Wifi,
  Building2,
  GraduationCap,
  Heart,
  Shield,
  Plane,
  Truck,
  CalendarDays,
  Plus,
  Trash2,
  Edit2,
  Save,
  X
} from 'lucide-react';
import api from '../lib/api';

// Default PCS checklist categories and items.
// Item/category ids are persistence keys for saved progress — never change them.
// Names/descriptions are platform-authored copy and come from the i18n system
// (Batch 27); fallbacks preserve the original English.
export function buildDefaultChecklist(t) {
  return [
    {
      id: 'pre-departure',
      name: t('pcs_checklist.cat.pre_departure', 'Pre-Departure'),
      icon: Plane,
      color: 'text-blue-400',
      items: [
        { id: 'orders', name: t('pcs_checklist.item.orders.name', 'Receive PCS Orders'), description: t('pcs_checklist.item.orders.desc', 'Get official orders from your unit'), required: true },
        { id: 'housing-office', name: t('pcs_checklist.item.housing_office.name', 'Visit Housing Office'), description: t('pcs_checklist.item.housing_office.desc', 'Schedule appointment with housing office'), required: true },
        { id: 'tla-approval', name: t('pcs_checklist.item.tla_approval.name', 'TLA Approval'), description: t('pcs_checklist.item.tla_approval.desc', 'Get TLA authorization if needed'), required: true },
        { id: 'passports', name: t('pcs_checklist.item.passports.name', 'Update Passports'), description: t('pcs_checklist.item.passports.desc', 'Ensure all family passports are valid'), required: true },
        { id: 'medical-records', name: t('pcs_checklist.item.medical_records.name', 'Request Medical Records'), description: t('pcs_checklist.item.medical_records.desc', 'Get copies of all medical records'), required: true },
        { id: 'school-records', name: t('pcs_checklist.item.school_records.name', 'Request School Records'), description: t('pcs_checklist.item.school_records.desc', 'Get transcripts and records for children'), required: false },
        { id: 'pet-paperwork', name: t('pcs_checklist.item.pet_paperwork.name', 'Pet Import Paperwork'), description: t('pcs_checklist.item.pet_paperwork.desc', 'Microchip, vaccinations, EU pet passport'), required: false },
      ]
    },
    {
      id: 'arrival',
      name: t('pcs_checklist.cat.arrival', 'First Week'),
      icon: Building2,
      color: 'text-emerald-400',
      items: [
        { id: 'sponsor-contact', name: t('pcs_checklist.item.sponsor_contact.name', 'Contact Sponsor'), description: t('pcs_checklist.item.sponsor_contact.desc', 'Connect with your assigned sponsor'), required: true },
        { id: 'check-in', name: t('pcs_checklist.item.check_in.name', 'Unit Check-In'), description: t('pcs_checklist.item.check_in.desc', 'Report to your new unit'), required: true },
        { id: 'tla-checkin', name: t('pcs_checklist.item.tla_checkin.name', 'TLA Check-In'), description: t('pcs_checklist.item.tla_checkin.desc', 'Check into TLA housing'), required: true },
        { id: 'bank-account', name: t('pcs_checklist.item.bank_account.name', 'Open Bank Account'), description: t('pcs_checklist.item.bank_account.desc', 'Set up German bank account (Sparkasse, etc.)'), required: true },
        { id: 'phone-plan', name: t('pcs_checklist.item.phone_plan.name', 'Get Phone Plan'), description: t('pcs_checklist.item.phone_plan.desc', 'Set up German mobile phone'), required: false },
      ]
    },
    {
      id: 'documents',
      name: t('pcs_checklist.cat.documents', 'Documents & Registration'),
      icon: FileText,
      color: 'text-amber-400',
      items: [
        { id: 'sofa-stamp', name: t('pcs_checklist.item.sofa_stamp.name', 'SOFA Stamp'), description: t('pcs_checklist.item.sofa_stamp.desc', 'Get SOFA stamp in passport'), required: true },
        { id: 'ration-card', name: t('pcs_checklist.item.ration_card.name', 'Ration Card'), description: t('pcs_checklist.item.ration_card.desc', 'Apply for ration card privileges'), required: true },
        { id: 'id-cards', name: t('pcs_checklist.item.id_cards.name', 'ID Cards'), description: t('pcs_checklist.item.id_cards.desc', 'Get dependent ID cards if needed'), required: true },
        { id: 'drivers-license', name: t('pcs_checklist.item.drivers_license.name', 'USAREUR License'), description: t('pcs_checklist.item.drivers_license.desc', "Get USAREUR driver's license"), required: true },
        { id: 'vehicle-registration', name: t('pcs_checklist.item.vehicle_registration.name', 'Vehicle Registration'), description: t('pcs_checklist.item.vehicle_registration.desc', 'Register POV or rental vehicle'), required: false },
      ]
    },
    {
      id: 'housing',
      name: t('pcs_checklist.cat.housing', 'Permanent Housing'),
      icon: Home,
      color: 'text-purple-400',
      items: [
        { id: 'house-hunting', name: t('pcs_checklist.item.house_hunting.name', 'House Hunting'), description: t('pcs_checklist.item.house_hunting.desc', 'Search for permanent housing'), required: true },
        { id: 'sign-lease', name: t('pcs_checklist.item.sign_lease.name', 'Sign Lease'), description: t('pcs_checklist.item.sign_lease.desc', 'Sign rental contract'), required: true },
        { id: 'utilities-setup', name: t('pcs_checklist.item.utilities_setup.name', 'Set Up Utilities'), description: t('pcs_checklist.item.utilities_setup.desc', 'Electricity, gas, water'), required: true },
        { id: 'internet', name: t('pcs_checklist.item.internet.name', 'Internet Setup'), description: t('pcs_checklist.item.internet.desc', 'Order internet service'), required: true },
        { id: 'hho-pickup', name: t('pcs_checklist.item.hho_pickup.name', 'HHG Delivery'), description: t('pcs_checklist.item.hho_pickup.desc', 'Schedule household goods delivery'), required: true },
      ]
    },
    {
      id: 'family',
      name: t('pcs_checklist.cat.family', 'Family Setup'),
      icon: Heart,
      color: 'text-red-400',
      items: [
        { id: 'school-enrollment', name: t('pcs_checklist.item.school_enrollment.name', 'School Enrollment'), description: t('pcs_checklist.item.school_enrollment.desc', 'Enroll children in DoDEA or local school'), required: false },
        { id: 'childcare', name: t('pcs_checklist.item.childcare.name', 'Childcare Registration'), description: t('pcs_checklist.item.childcare.desc', 'Register for CDC or FCC'), required: false },
        { id: 'tricare', name: t('pcs_checklist.item.tricare.name', 'TRICARE Setup'), description: t('pcs_checklist.item.tricare.desc', 'Enroll in TRICARE Prime/Select'), required: true },
        { id: 'dental', name: t('pcs_checklist.item.dental.name', 'Dental Enrollment'), description: t('pcs_checklist.item.dental.desc', 'Set up dental coverage'), required: true },
        { id: 'spouse-employment', name: t('pcs_checklist.item.spouse_employment.name', 'Spouse Employment'), description: t('pcs_checklist.item.spouse_employment.desc', 'Explore SOFA job opportunities'), required: false },
      ]
    },
    {
      id: 'insurance',
      name: t('pcs_checklist.cat.insurance', 'Insurance & Safety'),
      icon: Shield,
      color: 'text-cyan-400',
      items: [
        { id: 'car-insurance', name: t('pcs_checklist.item.car_insurance.name', 'Car Insurance'), description: t('pcs_checklist.item.car_insurance.desc', 'Get German auto insurance'), required: true },
        { id: 'liability', name: t('pcs_checklist.item.liability.name', 'Liability Insurance'), description: t('pcs_checklist.item.liability.desc', 'Personal liability (Haftpflicht)'), required: true },
        { id: 'renters-insurance', name: t('pcs_checklist.item.renters_insurance.name', 'Renters Insurance'), description: t('pcs_checklist.item.renters_insurance.desc', 'Coverage for your belongings'), required: false },
        { id: 'emergency-contacts', name: t('pcs_checklist.item.emergency_contacts.name', 'Emergency Contacts'), description: t('pcs_checklist.item.emergency_contacts.desc', 'Save local emergency numbers'), required: true },
      ]
    }
  ];
}

export default function PCSChecklistPage() {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const checklist = useMemo(() => buildDefaultChecklist(t), [t]);
  const [completedItems, setCompletedItems] = useState({});
  const [notes, setNotes] = useState({});
  const [arrivalDate, setArrivalDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [tempNote, setTempNote] = useState('');
  const [pcsDates, setPcsDates] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);
  const latestChecklistRef = useRef({
    completed: {},
    notes: {},
    arrivalDate: ''
  });

  useEffect(() => {
    if (user) {
      loadChecklist();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Load PCS dates when arrival date changes
  useEffect(() => {
    if (arrivalDate && user) {
      loadPcsDates(arrivalDate);
    }
  }, [arrivalDate, user]);

  const loadChecklist = async () => {
    try {
      const res = await api.get('/user/pcs-checklist');
      if (res.data) {
        const nextCompleted = res.data.completed || {};
        const nextNotes = res.data.notes || {};
        const nextArrivalDate = res.data.arrival_date || '';
        latestChecklistRef.current = {
          completed: nextCompleted,
          notes: nextNotes,
          arrivalDate: nextArrivalDate
        };
        setCompletedItems(nextCompleted);
        setNotes(nextNotes);
        setArrivalDate(nextArrivalDate);
        // Load PCS dates if arrival date is set
        if (nextArrivalDate) {
          loadPcsDates(nextArrivalDate);
        }
      }
    } catch (error) {
      console.error('Failed to load checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPcsDates = async (date) => {
    if (!date || !user) return;
    setLoadingDates(true);
    try {
      const res = await api.get(`/user/pcs-checklist/dates?arrival_date=${date}`);
      setPcsDates(res.data);
    } catch (error) {
      console.error('Failed to load PCS dates:', error);
    } finally {
      setLoadingDates(false);
    }
  };

  const saveChecklist = async () => {
    if (!user) return;

    const {
      completed,
      notes: latestNotes,
      arrivalDate: latestArrivalDate
    } = latestChecklistRef.current;
    
    setSaving(true);
    try {
      await api.put('/user/pcs-checklist', {
        completed,
        notes: latestNotes,
        arrival_date: latestArrivalDate
      });
    } catch (error) {
      console.error('Failed to save checklist:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (itemId) => {
    const currentCompleted = latestChecklistRef.current.completed || {};
    const updated = { ...currentCompleted, [itemId]: !currentCompleted[itemId] };
    latestChecklistRef.current = { ...latestChecklistRef.current, completed: updated };
    setCompletedItems(updated);
    // Auto-save after toggle
    setTimeout(() => saveChecklist(), 500);
  };

  const saveNote = (itemId) => {
    const currentNotes = latestChecklistRef.current.notes || {};
    const updated = { ...currentNotes, [itemId]: tempNote };
    latestChecklistRef.current = { ...latestChecklistRef.current, notes: updated };
    setNotes(updated);
    setEditingNote(null);
    setTempNote('');
    setTimeout(() => saveChecklist(), 500);
  };

  const calculateProgress = () => {
    const allItems = checklist.flatMap(cat => cat.items);
    const requiredItems = allItems.filter(item => item.required);
    const completedRequired = requiredItems.filter(item => completedItems[item.id]).length;
    const completedTotal = allItems.filter(item => completedItems[item.id]).length;
    
    return {
      requiredProgress: requiredItems.length > 0 ? Math.round((completedRequired / requiredItems.length) * 100) : 0,
      totalProgress: allItems.length > 0 ? Math.round((completedTotal / allItems.length) * 100) : 0,
      completedRequired,
      totalRequired: requiredItems.length,
      completedTotal,
      total: allItems.length
    };
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-signal-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="pcs-checklist-page">
      {/* Hero */}
      <div className="bg-gradient-to-b from-champagne-100 to-ivory border-b border-champagne-200 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-signal-blue/10 border border-signal-blue/30 rounded-full mb-3">
                <CalendarDays className="w-4 h-4 text-signal-blue" />
                <span className="text-signal-blue text-sm">{t('pcs_checklist.tracker', 'PCS Tracker')}</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2 tracking-tight">
                {t('pcs_checklist.title', 'PCS CHECKLIST')}
              </h1>
              <p className="text-slate-600">
                {t('pcs_checklist.subtitle', 'Track your move to Europe. Never miss an important step.')}
              </p>
            </div>

            {/* Progress Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 min-w-[280px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">{t('pcs_checklist.required_tasks', 'Required Tasks')}</span>
                <span className="text-lg font-bold text-military-gold">{progress.requiredProgress}%</span>
              </div>
              <Progress value={progress.requiredProgress} className="h-2 mb-3" />
              <div className="flex items-center justify-between text-xs text-slate-800">
                <span>{progress.completedRequired}/{progress.totalRequired} {t('pcs_checklist.required', 'required')}</span>
                <span>{progress.completedTotal}/{progress.total} {t('pcs_checklist.total', 'total')}</span>
              </div>
              
              {!user && (
                <div className="mt-4 pt-4 border-t border-slate-300">
                  <p className="text-xs text-slate-800 mb-2">{t('pcs_checklist.sign_in_to_save', 'Sign in to save your progress')}</p>
                  <Button
                    size="sm"
                    onClick={() => navigate('/login?redirect=/pcs-checklist')}
                    className="w-full bg-signal-blue hover:bg-signal-blue-hover"
                  >
                    {t('pcs_checklist.sign_in_cta', 'Sign In to Save')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PCS Timeline - Date-based automation */}
      {user && (
        <div className="bg-white/50 py-6 border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* Arrival Date Picker */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:w-72">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <CalendarDays className="w-4 h-4 inline mr-2" />
                  {t('pcs_checklist.expected_arrival_date', 'Expected Arrival Date')}
                </label>
                <input
                  type="date"
                  value={arrivalDate}
                  onChange={(e) => {
                    const nextArrivalDate = e.target.value;
                    latestChecklistRef.current = { ...latestChecklistRef.current, arrivalDate: nextArrivalDate };
                    setArrivalDate(nextArrivalDate);
                    saveChecklist();
                  }}
                  className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-800"
                  data-testid="arrival-date-input"
                />
                {pcsDates && (
                  <div className="mt-3 text-center">
                    <span className="text-2xl font-bold text-military-gold">{pcsDates.days_until_arrival}</span>
                    <span className="text-slate-600 text-sm ml-2">{t('pcs_checklist.days_to_go', 'days to go')}</span>
                  </div>
                )}
              </div>

              {/* Timeline */}
              {pcsDates && pcsDates.milestones && (
                <div className="flex-1 overflow-x-auto">
                  <div className="flex gap-3 min-w-max pb-2">
                    {pcsDates.milestones.map((milestone, idx) => (
                      <div 
                        key={milestone.id}
                        className={`relative flex-shrink-0 w-36 p-3 rounded-lg border transition-colors ${
                          milestone.passed 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : pcsDates.next_milestone?.id === milestone.id
                            ? 'bg-military-gold/10 border-military-gold/50 ring-1 ring-military-gold/30'
                            : 'bg-white border-slate-300'
                        }`}
                      >
                        <div className="text-xs text-slate-800 mb-1">{milestone.name}</div>
                        <div className={`text-sm font-bold ${
                          milestone.passed ? 'text-emerald-400' : 
                          pcsDates.next_milestone?.id === milestone.id ? 'text-military-gold' : 'text-slate-600'
                        }`}>
                          {new Date(milestone.date).toLocaleDateString(locale, { month: 'short', day: 'numeric' })}
                        </div>
                        {!milestone.passed && milestone.days_remaining > 0 && (
                          <div className="text-xs text-slate-800 mt-1">
                            {milestone.days_remaining}{t('pcs_checklist.days_left_suffix', 'd left')}
                          </div>
                        )}
                        {milestone.passed && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute top-2 right-2" />
                        )}
                        {/* Connector line */}
                        {idx < pcsDates.milestones.length - 1 && (
                          <div className={`absolute top-1/2 -right-3 w-3 h-0.5 ${
                            milestone.passed ? 'bg-emerald-500/50' : 'bg-slate-700'
                          }`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Next Milestone Tasks */}
            {pcsDates?.next_milestone && (
              <div className="mt-4 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-military-gold" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('pcs_checklist.coming_up', 'Coming up:')} {pcsDates.next_milestone.name}
                  </span>
                  <span className="text-xs text-military-gold ml-auto">
                    {pcsDates.next_milestone.days_remaining} {t('pcs_checklist.days', 'days')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pcsDates.next_milestone.tasks.map((task, idx) => (
                    <span key={idx} className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">
                      {task}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {checklist.map((category) => (
            <div key={category.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Category Header */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-200">
                <category.icon className={`w-5 h-5 ${category.color}`} />
                <h2 className="font-heading text-lg font-bold text-slate-800">{category.name}</h2>
                <Badge variant="outline" className="ml-auto text-xs">
                  {category.items.filter(item => completedItems[item.id]).length}/{category.items.length}
                </Badge>
              </div>

              {/* Items */}
              <div className="divide-y divide-slate-200">
                {category.items.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 transition-colors ${
                      completedItems[item.id] ? 'bg-emerald-500/5' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleItem(item.id)}
                        className={`mt-0.5 flex-shrink-0 p-2 -m-2 transition-colors ${
                          completedItems[item.id]
                            ? 'text-emerald-500'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                        aria-label={item.name}
                        data-testid={`check-${item.id}`}
                      >
                        {completedItems[item.id] ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <Circle className="w-5 h-5" />
                        )}
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-medium ${
                            completedItems[item.id] ? 'text-slate-800 line-through' : 'text-slate-800'
                          }`}>
                            {item.name}
                          </span>
                          {item.required && (
                            <Badge className="text-[10px] px-1.5 py-0 bg-red-500/20 text-red-400 border-red-500/30">
                              {t('pcs_checklist.required_badge', 'Required')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-800">{item.description}</p>

                        {/* Notes */}
                        {editingNote === item.id ? (
                          <div className="mt-2 flex gap-2">
                            <input
                              type="text"
                              value={tempNote}
                              onChange={(e) => setTempNote(e.target.value)}
                              placeholder={t('pcs_checklist.add_note_placeholder', 'Add a note...')}
                              className="flex-1 text-sm bg-white border border-slate-300 rounded px-2 py-1 text-slate-800"
                              autoFocus
                            />
                            <Button size="sm" variant="ghost" onClick={() => saveNote(item.id)}>
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : notes[item.id] ? (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-slate-800 italic">"{notes[item.id]}"</span>
                            <button
                              onClick={() => { setEditingNote(item.id); setTempNote(notes[item.id]); }}
                              className="text-slate-600 hover:text-slate-600"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingNote(item.id); setTempNote(''); }}
                            className="mt-2 text-xs text-slate-600 hover:text-slate-600 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> {t('pcs_checklist.add_note', 'Add note')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Save Indicator */}
        {user && saving && (
          <div className="fixed bottom-4 right-4 bg-slate-800 text-slate-300 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-signal-blue border-t-transparent rounded-full" />
            {t('pcs_checklist.saving', 'Saving...')}
          </div>
        )}
      </div>
    </div>
  );
}
